import { ColDef, ICellRendererParams, themeAlpine } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import {
  IgAzurePrimitives,
  IgCustomTheme,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import { IgFlipChip } from "@intelligems/ig-theme/IgFlipChip";
import { CombinedCustomEvents, CustomEventDto } from "@intelligems/ig-types";
import { CircularProgress, Grid, GridProps, styled } from "@mui/material";
import { useTheme } from "@mui/styles";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { numberFormatter } from "@src/features/analytics/dataFormatters";
import { sortEventsManager } from "@src/features/analytics/tables/analyticsSegmentTable/utils";
import { MoreMenuEventsManager } from "@src/features/eventsManager/components/MoreMenuEventsManager";
import {
  initCustomEvent,
  initNewCustomEvent,
  selectedArchivingEventId,
  selectedUnarchivingEventId,
  setCustomJsEventIdentifier,
  setUnarchivingEventId,
} from "@src/store/slices/customEvents";
import { openDrawer } from "@src/store/slices/drawers";
import { setDialog } from "@src/store/slices/global";
import { theme } from "@src/theme";
import { dateTimeConverter, getTimeAgo } from "@src/utils/dateConverters";
import { useIsMutating } from "@tanstack/react-query";

import { useFilterEventsManagerData } from "../utils";

const myTheme = themeAlpine.withParams({
  fontFamily: theme.typography.fontFamily,
});

const prettyTypes = {
  pageView: "Page Visit",
  productPageView: "Product Page Visit",
  collectionPageView: "Collection Page Visit",
  clickEvent: "Click",
  elementViewed: "Element Viewed",
  scrollDepth: "Scroll",
  javascriptEvent: "JavaScript",
  javascriptImportedEvent: "JavaScript",
};

interface StyledGridProps extends GridProps {
  searchInput: string;
}

const StyledGrid = styled(({ searchInput, ...props }: StyledGridProps) => (
  <Grid {...props} />
))(({ theme, searchInput }) => ({
  [`& .ag-root-wrapper`]: {
    border: `solid 1px ${theme.igBorders.core.default} !important`,
    borderTopLeftRadius: "8px",
    borderTopRightRadius: "8px",
    borderBottomLeftRadius: "8px",
    borderBottomRightRadius: "8px",
    fontFamily: `${theme.typography.fontFamily}`,
    fontSize: "14px",
    fontWeight: 400,
  },
  [`& .ag-center-cols-viewport`]: {
    minHeight: searchInput && searchInput !== "" ? "unset !important" : "100px",
  },
  [`& .ag-header-row`]: {
    backgroundColor: IgAzurePrimitives[100],
  },
  [`& .ag-header-cell-text`]: {
    fontSize: "14px",
    fontWeight: "600",
  },
  [`& .ag-group-value`]: {
    width: "100%",
  },
  [`& .ag-header`]: {
    borderBottom: "solid 0px !important",
  },
  [`& .ag-header-cell-label`]: {
    justifyContent: "flex-start",
    fontWeight: 400,
    marginBottom: "-5px",
  },
  [`& .ag-row`]: {
    // borderBottom: "2px solid #F5F5F5",
    // backgroundColor: "white",
  },
  [`& .ag-row-hovered`]: {
    textDecoration: "underline",
    cursor: "pointer",
  },
  [`& .right-aligned-cell`]: {
    justifyContent: "flex-end !important",
  },
}));

const LoadingTemplate = () => {
  return (
    <Grid
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        marginTop: "50px",
      }}
    >
      <CircularProgress size={30} style={{ marginRight: "10px" }} />
    </Grid>
  );
};

const NoRowsTemplate = () => {
  return (
    <Grid
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <IgTypography>No Custom Events Found</IgTypography>
    </Grid>
  );
};

interface EventsManagerTableProps {
  combinedCustomEvents: CombinedCustomEvents;
  showArchived: boolean;
  setShowArchived: (isArchived: boolean) => void;
  searchInput: string;
  isCombinedCustomEventsMutating: number;
}

export const EventsManagerTable = ({
  combinedCustomEvents,
  showArchived,
  setShowArchived,
  searchInput,
  isCombinedCustomEventsMutating,
}: EventsManagerTableProps) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const org = useGetOrg();
  const now = useMemo(
    () => new Date().getTime(),
    [isCombinedCustomEventsMutating]
  );
  const archivingEventId = useSelector(selectedArchivingEventId);
  const unarchivingEventId = useSelector(selectedUnarchivingEventId);
  const isArchiveMutating = useIsMutating({
    mutationKey: ["archiveCustomEvent"],
  });
  const isUnarchiveMutating = useIsMutating({
    mutationKey: ["unarchiveCustomEvent"],
  });
  const [hoveredRowIndex, setHoveredRowIndex] = useImmer<number | null>(null);
  const [activeRowIndex, setActiveRowIndex] = useImmer<number | null>(null);
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");
  // only show one loading state at a time
  const isLoading =
    isCombinedCustomEventsMutating > 0 &&
    !isUnarchiveMutating &&
    !isArchiveMutating;

  const onNameClick = (eventData: CustomEventDto) => {
    dispatch(
      initCustomEvent({
        customEvent: eventData,
      })
    );
    dispatch(
      openDrawer({
        type: "customEvents",
        isEdit: true,
      })
    );
  };

  const unregisteredTooltipMessage = (
    <IgTypography>
      <IgTypography fontWeight={600}>Unregistered Event</IgTypography>
      <br />A custom javascript event added directly in your Shopify theme file
      does not yet have a name or description. Click “Register” to give it a
      name and description. Once registered you can track it in your tests.
    </IgTypography>
  );

  const archivedTooltipMessage = (
    <IgTypography>
      <IgTypography fontWeight={600}>Not in Use</IgTypography>
      <br />
      Not included as a tracked metric in any pending, active, or paused tests.
    </IgTypography>
  );

  const successTooltipMessage = (
    <IgTypography>
      <IgTypography fontWeight={600}>In Use</IgTypography>
      {/*TODO: Add in tracked tests here when BE is supporting that*/}
      {/*<br />*/}
      {/*This is a tracked metric in:*/}
    </IgTypography>
  );

  const filteredRowData = useFilterEventsManagerData(
    combinedCustomEvents,
    searchInput,
    showArchived,
    setShowArchived
  );

  const filteredAndSortedRowData = useMemo(() => {
    return sortEventsManager(filteredRowData);
  }, [filteredRowData, isCombinedCustomEventsMutating]);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      resizable: false,
      cellStyle: (params) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        fontSize: "14px",
        height: "100%",
        color: params.data?.archivedAtTs
          ? theme.palette.text.tertiary
          : theme.palette.text.primary,
      }),
      menuTabs: [],
      suppressMovable: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      suppressHeaderMenuButton: true,
      sortable: false,
    };
  }, []);

  const columnDefs: ColDef[] = useMemo(() => {
    return [
      // {
      //   headerName: "",
      //   field: "",
      //   width: 65,
      //   // eslint-disable-next-line react/no-unstable-nested-components
      //   cellRenderer: (params: ICellRendererParams) => {
      //     if (!params.data.id) {
      //       return (
      //         <IgTooltip title={unregisteredTooltipMessage} placement={"right"}>
      //           <IgStatusCircle status={"danger"} noPulse />
      //         </IgTooltip>
      //       );
      //     }
      //     if (params.data.archivedAtTs) {
      //       return (
      //         <IgTooltip title={archivedTooltipMessage} placement={"right"}>
      //           <IgStatusCircle status={"info"} noPulse />
      //         </IgTooltip>
      //       );
      //     }
      //     return (
      //       <IgTooltip title={successTooltipMessage} placement={"right"}>
      //         <IgStatusCircle status={"success"} noPulse />
      //       </IgTooltip>
      //     );
      //   },
      // },
      {
        headerName: "Name",
        field: "name",
        flex: 3,
        cellClassRules: {
          "ag-row-hovered": (params) =>
            params.rowIndex === hoveredRowIndex && "id" in params.data,
        },
        valueGetter: (params) => {
          return "id" in params.data ? params.data.name : params.data.event_id;
        },
        onCellClicked: (params) => {
          if ("id" in params.data) {
            onNameClick(params.data);
          }
        },
      },
      {
        headerName: "Type",
        field: "type",
        flex: 1.8,
        cellRenderer: (params: ICellRendererParams) => {
          return prettyTypes[params.value];
        },
      },
      {
        headerName: "Last Triggered (<24HR)",
        field: "last_event_ts",
        flex: 2,
        valueGetter: (params) => {
          return "id" in params.data
            ? params.data.tbEvent?.last_event_ts
            : params.data.last_event_ts;
        },
        cellRenderer: (params: ICellRendererParams) => {
          return getTimeAgo(params.value, now);
        },
      },
      {
        headerName: "24HR Count",
        field: "n_occurrences",
        valueGetter: (params) => {
          return "id" in params.data
            ? params.data.tbEvent?.n_occurrences
              ? numberFormatter(
                  params.data.tbEvent?.n_occurrences,
                  false,
                  storeCurrencyConfig!.value,
                  undefined,
                  false
                )
              : ""
            : params.data.n_occurrences
              ? numberFormatter(
                  params.data.n_occurrences,
                  false,
                  storeCurrencyConfig!.value,
                  undefined,
                  false
                )
              : "";
        },
        flex: 1.5,
      },
      {
        headerName: "Created",
        field: "createdAtTs",
        flex: 1.25,
        valueGetter: (params) => {
          return "id" in params.data ? params.data.createdAtTs : "";
        },
        cellRenderer: (params: ICellRendererParams) => {
          return dateTimeConverter(
            params.value,
            storeCurrencyConfig?.value?.locale ?? "en-US",
            org?.data?.org?.timezone ?? "UTC"
          );
        },
      },
      {
        headerName: "",
        field: "",
        cellClass: "right-aligned-cell",
        flex: 1.5,
        // eslint-disable-next-line react/no-unstable-nested-components
        cellRenderer: (params: ICellRendererParams) => {
          if (!params.data.id) {
            return (
              <IgTooltip
                title={
                  "This event is firing from your shop’s theme code but isn’t yet registered.\nClick to register it so you can track it during your tests. "
                }
                maxWidth={200}
                sx={{ zIndex: 999 }}
              >
                <IgFlipChip
                  label={"Unregistered"}
                  color={"error"}
                  hoverText={"Register"}
                  onClick={() => {
                    dispatch(
                      initNewCustomEvent({
                        eventType: "javascriptImportedEvent",
                      })
                    );
                    dispatch(
                      setCustomJsEventIdentifier({
                        identifier: params.data.event_id,
                      })
                    );
                    dispatch(
                      openDrawer({
                        type: "customEvents",
                        isEdit: true,
                      })
                    );
                  }}
                />
              </IgTooltip>
            );
          }
          if (params.data.archivedAtTs) {
            return (
              <IgFlipChip
                label={"Archived"}
                color={"default"}
                hoverText={"Unarchive"}
                onClick={() => {
                  dispatch(setUnarchivingEventId({ id: params.data.id }));
                  dispatch(
                    setDialog({ dialog: "unarchiveCustomEvent", open: true })
                  );
                }}
              />
            );
          }
          return null;
        },
        suppressRefresh: true,
      },
      {
        headerName: "",
        field: "",
        maxWidth: 80,
        // eslint-disable-next-line react/no-unstable-nested-components
        cellRenderer: (params: ICellRendererParams) => {
          const shouldShowMenu = "id" in params.data;
          if (isArchiveMutating && archivingEventId === params.data.id) {
            return <CircularProgress size={20} />;
          }
          if (isUnarchiveMutating && unarchivingEventId === params.data.id) {
            return <CircularProgress size={20} />;
          }

          return shouldShowMenu ? (
            <MoreMenuEventsManager
              onClose={() => setActiveRowIndex(null)}
              eventData={params.data}
              onEditClick={onNameClick}
            />
          ) : null;
        },
      },
    ];
  }, [
    hoveredRowIndex,
    activeRowIndex,
    combinedCustomEvents,
    isArchiveMutating,
    isUnarchiveMutating,
    now,
  ]);

  return (
    <>
      <StyledGrid
        className={"ag-theme-alpine"}
        sx={{
          width: "100%",
          marginTop: "20px",
        }}
        searchInput={searchInput}
      >
        <AgGridReact
          theme={myTheme}
          rowData={filteredAndSortedRowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          suppressDragLeaveHidesColumns={true}
          suppressExcelExport={true}
          suppressCsvExport={true}
          suppressContextMenu={true}
          suppressRowHoverHighlight={true}
          suppressCellFocus={true}
          domLayout={"autoHeight"}
          suppressNoRowsOverlay={searchInput !== ""}
          noRowsOverlayComponent={NoRowsTemplate}
          loading={isLoading}
          loadingOverlayComponent={LoadingTemplate}
          onCellMouseOver={(event) => {
            if (event.rowIndex !== undefined) {
              setHoveredRowIndex(event.rowIndex);
              setActiveRowIndex(event.rowIndex);
            }
          }}
          onCellMouseOut={(event) => {
            if (event.rowIndex !== activeRowIndex) {
              setHoveredRowIndex(null);
              setActiveRowIndex(null);
            }
          }}
          rowClassRules={{
            "row-hovered": (params) => params.rowIndex === hoveredRowIndex,
          }}
        />
      </StyledGrid>
    </>
  );
};
